import { Icon } from "@/features/icons/components/IconLoader"
import { cn } from "@/utils/helpers"
import { faCheckCircle } from "@awesome.me/kit-44b29310a6/icons/classic/solid"
import { Label } from "../Label"
import {
  PhoneInputRefType,
  PhoneInput as ReactPhoneInput,
} from "react-international-phone"
import "react-international-phone/style.css"
import { localeCountryCode } from "./PhoneUtil"
import { useState, useMemo, useId, useEffect, forwardRef, Ref } from "react"
import { useTranslation } from "next-i18next"

export type PhoneInputProps = {
  label: string
  value?: string
  disabled?: boolean | null
  className?: string | null
  placeholder?: string | null
  error: string | null
  isCompleted?: boolean | null
  onChange: (
    phone: string,
    meta: {
      inputValue: string
      country: { dialCode: string; iso2: string; name: string }
    },
  ) => void
  tooltipText?: string
  /**
   * @default true
   */
  forceDialCode?: boolean
  disableDialCodePrefill?: boolean
}

export const PhoneInput = forwardRef(
  (
    {
      label,
      value,
      onChange,
      disabled = false,
      className = "",
      placeholder = "",
      error = null,
      isCompleted = false,
      tooltipText,
      forceDialCode = true,
      disableDialCodePrefill,
    }: PhoneInputProps,
    ref: Ref<PhoneInputRefType>,
  ) => {
    const { t } = useTranslation("common")
    const valueHasDialCode = useMemo(() => {
      if (!value || typeof value !== "string") return false
      return value?.startsWith("+") || value?.replace(/\s+/g, "").length >= 10
    }, [value])
    const [phone, setPhone] = useState(valueHasDialCode ? value : "")

    useEffect(() => {
      setPhone(valueHasDialCode ? value : "")
    }, [value, valueHasDialCode])

    const [country, setCountry] = useState<{
      dialCode: string
      iso2: string
      name: string
    }>({
      dialCode: "",
      iso2: "",
      name: "",
    })
    const [tooltipPos, setTooltipPos] = useState<{
      x: number
      y: number
    } | null>(null)
    const inputId = useId()

    const handleChange = (newPhone: string, meta: any) => {
      setPhone(newPhone)
      setCountry(meta.country)
      onChange(newPhone, { inputValue: newPhone, country: meta.country })
    }

    const showMissingDialCodeError = useMemo(() => {
      return (
        !valueHasDialCode &&
        value &&
        phone.length <= country.dialCode.length + 1
      )
    }, [valueHasDialCode, value, phone, country.dialCode.length])

    return (
      <div
        className="relative flex w-full flex-col gap-1"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
        }}
        onMouseLeave={() => setTooltipPos(null)}
      >
        <label
          className={cn(
            "text-on-surface-variant text-md w-fit text-left",
            (showMissingDialCodeError || error) && "text-error",
          )}
          htmlFor={inputId}
        >
          {label}
        </label>
        <div
          className={cn(
            "group relative flex",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <ReactPhoneInput
            disableDialCodePrefill={disableDialCodePrefill}
            defaultCountry={localeCountryCode.toLocaleLowerCase()}
            value={phone}
            onChange={handleChange}
            forceDialCode={forceDialCode}
            preferredCountries={["dk", "no", "se"]}
            className={cn(
              "placeholder:text-muted-foreground text-md group",
              "flex h-14 w-full items-center rounded py-1 transition-colors",
              className,
            )}
            inputProps={{
              className: cn(
                "border-none h-14 w-full rounded rounded-l-none px-3 py-1 text-base select-none outline-none bg-transparent focus:bg-transparent",
                disabled && "cursor-not-allowed",
              ),
              id: inputId,
            }}
            countrySelectorStyleProps={{
              buttonClassName: cn(
                "!bg-transparent !h-14 !w-14 !border-l-0 !border-t-0 !border-b-0 !border-r-1 !border-outline-variant",
                disabled && "cursor-not-allowed",
              ),
              dropdownStyleProps: { className: cn("z-20") },
            }}
            dialCodePreviewStyleProps={{ style: { backgroundColor: "blue" } }}
            disabled={disabled}
            placeholder={placeholder}
            ref={ref}
          />
          <div
            className={cn(
              "border-on-surface-variant group-focus-within:border-primary group-focus-within:border-3 pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-10 flex size-full rounded-[0.25rem] border",
              (showMissingDialCodeError || error) && "border-error border-2",
            )}
          ></div>
        </div>
        {isCompleted && (
          <Icon
            icon={faCheckCircle}
            className="absolute right-4 top-10 size-5 text-green-400"
          />
        )}
        {error && (
          <Label size="sm" className="text-error mt-1 pl-4">
            {error}
          </Label>
        )}
        {showMissingDialCodeError && (
          <Label
            size="sm"
            className="text-error mt-1 cursor-pointer pl-4 hover:underline"
            onClick={() => {
              const newPhone = country.dialCode + value
              setPhone(newPhone)
              onChange(newPhone, { inputValue: newPhone, country })
            }}
          >
            {t("phone_missing_dial_code", {
              phone:
                typeof value === "string" ? value.replace(/\s+/g, "") : value,
            })}
          </Label>
        )}
        {tooltipPos && tooltipText && (
          <p
            className="bg-on-surface pointer-events-none absolute z-50 rounded px-2 py-1 text-sm text-white"
            style={{
              left: tooltipPos.x + 15,
              top: tooltipPos.y + 20,
            }}
          >
            {tooltipText}
          </p>
        )}
      </div>
    )
  },
)

PhoneInput.displayName = "PhoneInput"
